import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Copy, Heart, Share2, Loader2, MessageSquareQuote } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

const TONES = [
  { id: "humour", label: "Humour", emoji: "😏" },
  { id: "sarcasme", label: "Sarcasme", emoji: "🌶️" },
  { id: "ferme", label: "Ferme", emoji: "✋" },
  { id: "empathique", label: "Empathique", emoji: "🤝" },
  { id: "diplomate", label: "Diplomate", emoji: "🕊️" },
];

const EXAMPLES = [
  "Tu devrais le laisser pleurer, ça lui forgera le caractère",
  "À mon époque, on faisait des nuits à 1 mois",
  "Tu vas le rendre capricieux si tu le portes tout le temps",
  "Il a froid, mets-lui un bonnet",
];

export default function Generator({ sessionId, api, onGenerated, onFavorite }) {
  const [advice, setAdvice] = useState("");
  const [tone, setTone] = useState("humour");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const generate = async () => {
    if (!advice.trim()) {
      toast.error("Tape d'abord le conseil reçu !");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const r = await axios.post(`${api}/generate`, {
        advice: advice.trim(),
        tone,
        session_id: sessionId,
      });
      setResult(r.data);
      onGenerated?.();
    } catch (e) {
      toast.error("Oups, l'IA fait la sieste. Réessaie.");
    } finally {
      setLoading(false);
    }
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copié ! Prêt à coller 📋");
      trackEvent("copy_click", { source: "generator" });
    } catch {
      toast.error("Copie impossible");
    }
  };

  const share = (text) => {
    const url = window.location.origin;
    const tweet = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text + " — via " + url)}`;
    window.open(tweet, "_blank", "noopener");
    trackEvent("share_click", { channel: "twitter", source: "generator" });
  };

  const shareWhatsApp = (text) => {
    const url = window.location.origin;
    const wa = `https://wa.me/?text=${encodeURIComponent(text + "\n\n— via " + url)}`;
    window.open(wa, "_blank", "noopener");
    trackEvent("share_click", { channel: "whatsapp", source: "generator" });
  };

  return (
    <section id="generator" className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <div className="lg:col-span-7 space-y-5">
        <div className="flex items-center gap-3">
          <MessageSquareQuote size={28} strokeWidth={2.5} />
          <h2 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl tracking-tight">
            Le conseil qui t'agace ?
          </h2>
        </div>

        <div className="nb-card p-5 lg:p-6 space-y-4" style={{ background: "#FFFDF9" }}>
          <label className="block">
            <span className="text-xs font-black uppercase tracking-wider mb-2 block">
              Colle ici la phrase reçue
            </span>
            <textarea
              data-testid="advice-input"
              value={advice}
              onChange={(e) => setAdvice(e.target.value)}
              rows={4}
              placeholder='Ex: "Tu vas le faire pourrir à le porter tout le temps..."'
              className="w-full bg-white nb-border p-4 font-medium focus:outline-none focus:shadow-[4px_4px_0_0_#FF4D85] transition-all resize-none"
              maxLength={500}
            />
            <span className="text-xs font-bold text-[#0A0A0A]/60 mt-1 block">
              {advice.length}/500
            </span>
          </label>

          <div className="flex flex-wrap gap-2">
            {EXAMPLES.map((ex, i) => (
              <button
                key={i}
                data-testid={`example-chip-${i}`}
                onClick={() => setAdvice(ex)}
                className="nb-border bg-white px-3 py-1.5 text-xs font-bold hover:bg-[#FCD34D] transition-colors"
              >
                {ex.slice(0, 35)}…
              </button>
            ))}
          </div>

          <div>
            <span className="text-xs font-black uppercase tracking-wider mb-2 block">
              Choisis le ton
            </span>
            <div className="flex flex-wrap gap-2">
              {TONES.map((t) => {
                const active = tone === t.id;
                return (
                  <button
                    key={t.id}
                    data-testid={`tone-${t.id}`}
                    onClick={() => setTone(t.id)}
                    className={
                      "nb-border px-4 py-2 font-black text-sm transition-all flex items-center gap-1.5 " +
                      (active
                        ? "bg-[#FF8A00] nb-shadow translate-x-[-1px] translate-y-[-1px]"
                        : "bg-white hover:bg-[#F3F4F6]")
                    }
                  >
                    <span>{t.emoji}</span> {t.label}
                  </button>
                );
              })}
            </div>
          </div>

          <button
            data-testid="generate-button"
            onClick={generate}
            disabled={loading}
            className="w-full sm:w-auto nb-border bg-[#FF4D85] text-[#0A0A0A] px-8 py-4 font-black text-lg nb-shadow hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[6px_6px_0_0_#0A0A0A] active:translate-x-0 active:translate-y-0 active:nb-shadow transition-all inline-flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-wait"
          >
            {loading ? (
              <>
                <Loader2 size={20} className="animate-spin" strokeWidth={3} />
                L'IA mijote tes répliques…
              </>
            ) : (
              <>
                <Sparkles size={20} strokeWidth={3} />
                Générer 3 répliques
              </>
            )}
          </button>
        </div>
      </div>

      <div className="lg:col-span-5 space-y-4" data-testid="results-panel">
        <h3 className="font-display font-black text-xl sm:text-2xl">
          {result ? "🎯 Tes répliques" : "Tes répliques apparaîtront ici"}
        </h3>

        {!result && !loading && (
          <div className="nb-border border-dashed bg-white/60 p-6 text-center text-[#0A0A0A]/60 font-medium">
            Tape un conseil, choisis un ton, on s'occupe du reste.
          </div>
        )}

        {loading && (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="nb-card p-5 animate-pulse">
                <div className="h-3 bg-[#0A0A0A]/10 w-1/3 mb-3" />
                <div className="h-3 bg-[#0A0A0A]/10 w-full mb-2" />
                <div className="h-3 bg-[#0A0A0A]/10 w-2/3" />
              </div>
            ))}
          </div>
        )}

        {result?.comebacks?.map((c, i) => {
          const colors = ["#FFD6E8", "#A7F3D0", "#FCD34D"];
          return (
            <article
              key={i}
              data-testid={`comeback-card-${i}`}
              className="nb-card p-5"
              style={{ background: colors[i % colors.length] }}
            >
              <div className="flex items-center justify-between mb-3">
                <span className="nb-border bg-white px-2 py-0.5 text-xs font-black uppercase">
                  {c.label}
                </span>
                <span className="text-xs font-black opacity-60">#{i + 1}</span>
              </div>
              <p className="font-bold text-base lg:text-lg leading-snug mb-4">
                « {c.text} »
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  data-testid={`copy-button-${i}`}
                  onClick={() => copyText(c.text)}
                  className="nb-border bg-white px-3 py-1.5 text-sm font-bold hover:bg-[#0A0A0A] hover:text-white transition-colors flex items-center gap-1.5"
                >
                  <Copy size={14} strokeWidth={2.5} /> Copier
                </button>
                <button
                  data-testid={`fav-button-${i}`}
                  onClick={() => {
                    onFavorite(c.text, result.advice, result.tone);
                    trackEvent("favorite_add", { source: "generator", tone: result.tone });
                  }}
                  className="nb-border bg-white px-3 py-1.5 text-sm font-bold hover:bg-[#FF4D85] transition-colors flex items-center gap-1.5"
                >
                  <Heart size={14} strokeWidth={2.5} /> Favori
                </button>
                <button
                  data-testid={`share-button-${i}`}
                  onClick={() => share(c.text)}
                  className="nb-border bg-white px-3 py-1.5 text-sm font-bold hover:bg-[#A7F3D0] transition-colors flex items-center gap-1.5"
                >
                  <Share2 size={14} strokeWidth={2.5} /> X
                </button>
                <button
                  data-testid={`whatsapp-button-${i}`}
                  onClick={() => shareWhatsApp(c.text)}
                  className="nb-border bg-white px-3 py-1.5 text-sm font-bold hover:bg-[#25D366] hover:text-white transition-colors flex items-center gap-1.5"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M20.52 3.48A11.94 11.94 0 0 0 12 0C5.37 0 0 5.37 0 12c0 2.11.55 4.17 1.6 6L0 24l6.18-1.62A11.93 11.93 0 0 0 12 24c6.63 0 12-5.37 12-12 0-3.2-1.25-6.21-3.48-8.52Zm-8.52 18.4a9.9 9.9 0 0 1-5.06-1.39l-.36-.22-3.66.96.98-3.57-.24-.37A9.9 9.9 0 1 1 12 21.88Zm5.42-7.42c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.27-.47-2.42-1.5-.9-.8-1.5-1.78-1.67-2.08-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51l-.57-.01a1.1 1.1 0 0 0-.8.37c-.27.3-1.05 1.03-1.05 2.5s1.07 2.9 1.22 3.1c.15.2 2.1 3.2 5.1 4.49.71.31 1.27.5 1.7.64.71.23 1.36.2 1.87.12.57-.08 1.76-.72 2.01-1.41.25-.7.25-1.3.17-1.42-.07-.12-.27-.2-.57-.35Z"/>
                  </svg>
                  WhatsApp
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
